import React, { useState } from 'react';
import {
  ConfigProvider,
  Flex,
  message,
  Table,
  Tag,
  theme,
  Typography,
} from 'antd';
import type { TableColumnsType, TableProps } from 'antd';
import { Button } from 'antd';
import { useConventions } from '../../hooks/useConventions';
import { LuDownload } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { FilterFilled } from '@ant-design/icons';
import { DownloadRegisteredConventionsZip } from '../../helper/apiHelper';
import { format } from 'date-fns';
import {
  useColumnSearchPropsWithSearchField,
  useDateFilter,
} from '../../hooks/useColumnSearch';
import { downloadDocuments } from '../../Utils/downloadWithUrl';

type OperationType = 'download' | 'viewPdf';

interface DataType {
  key: React.Key;
  registrationNumber: string;
  studentName: string;
  studentGroup: string;
  organization: string;
  tutor: string;
  registrationDate: Date;
  internshipStartDate: Date;
  internshipEndDate: Date;
  isFinished: boolean;
  operations: OperationType[];
}

export const RegisteredConventionsTable = (): React.ReactElement => {
  const { data: registeredConventions } = useConventions();
  const [messageApi, contextHolder] = message.useMessage();
  const [selectedRows, setSelectedRows] = useState<DataType[]>([]);
  const [selectedRowCount, setSelectedRowCount] = useState<number>(0);
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { getColumnSearchPropsWithSearchField } =
    useColumnSearchPropsWithSearchField();
  const { getDateColumnSearchProps } = useDateFilter();

  const handleDownloadConventionDocument = async (
    conventionDocumentId: React.Key
  ): Promise<void> => {
    const convention = registeredConventions?.find(
      (convention) => convention.conventionId === conventionDocumentId
    );
    const rawViewUrl = convention?.conventionDocumentUrl;
    if (!rawViewUrl) {
      return Promise.resolve();
    }
    const statusConvention = convention.rectorSigned
      ? t('legDepComp.finished')
      : t('legDepComp.processing');
    const viewUrl = `${rawViewUrl}?status=${statusConvention}`;
    window.open(viewUrl, '_blank', 'noopener,noreferrer');
  };

  const filtersForStatus = [
    { text: t('legDepComp.finished'), value: true },
    { text: t('legDepComp.processing'), value: false },
  ];

  const columns: TableColumnsType<DataType> = [
    {
      title: t('legDepComp.registrationNumber'),
      dataIndex: 'registrationNumber',
      align: 'center',
      width: '8%',
      ...getColumnSearchPropsWithSearchField(
        t('legDepComp.registrationNumberPlaceholder'),
        'registrationNumber'
      ),
    },
    {
      title: t('convSignComp.StudentName'),
      dataIndex: 'studentName',
      align: 'center',
      ...getColumnSearchPropsWithSearchField(
        t('legDepComp.studentNamePlaceholder'),
        'studentName'
      ),
    },
    {
      title: t('convSignComp.studentGroup'),
      dataIndex: 'studentGroup',
      align: 'center',
      width: '8%',
      ...getColumnSearchPropsWithSearchField(
        t('convSignComp.studyGroupPlaceholder'),
        'studentGroup'
      ),
    },
    {
      title: t('legDepComp.organization'),
      dataIndex: 'organization',
      align: 'center',
      ...getColumnSearchPropsWithSearchField(
        t('legDepComp.orgNamePlaceholder'),
        'organization'
      ),
    },
    {
      title: t('legDepComp.tutor'),
      dataIndex: 'tutor',
      align: 'center',
      ...getColumnSearchPropsWithSearchField(
        t('legDepComp.tutorNamePlaceholder'),
        'tutor'
      ),
    },
    {
      title: t('legDepComp.regDate'),
      dataIndex: 'registrationDate',
      align: 'center',
      width: '10%',
      ...getDateColumnSearchProps('registrationDate'),
    },
    {
      title: t('convSignComp.internshipStartDate'),
      dataIndex: 'internshipStartDate',
      align: 'center',
      width: '10%',
      ...getDateColumnSearchProps('internshipStartDate'),
    },
    {
      title: t('convSignComp.internshipEndDate'),
      dataIndex: 'internshipEndDate',
      align: 'center',
      width: '10%',
      ...getDateColumnSearchProps('internshipEndDate'),
    },
    {
      title: t('legDepComp.status'),
      dataIndex: 'status',
      align: 'center',
      filters: filtersForStatus,
      onFilter: (value, record) => record.isFinished === value,
      filterIcon: (filtered: boolean) => (
        <FilterFilled
          style={{ fontSize: '15px', color: filtered ? '#1677ff' : undefined }}
        />
      ),
      render: (_, record) => {
        return (
          <Tag color={record.isFinished ? 'green' : 'blue'}>
            {record.isFinished
              ? t('legDepComp.finished')
              : t('legDepComp.processing')}
          </Tag>
        );
      },
    },
    {
      title: t('legDepComp.operations'),
      dataIndex: 'operations',
      align: 'center',
      width: '5%',
      render: (operations: OperationType[], record: DataType) => {
        return (
          <Flex justify="center" gap="10px">
            {operations.map((operation) => {
              if (operation === 'download') {
                return (
                  <LuDownload
                    key={operation}
                    style={{ cursor: 'pointer', fontSize: 20 }}
                    onClick={() => handleDownloadConventionDocument(record.key)}
                  />
                );
              }
            })}
          </Flex>
        );
      },
    },
  ];

  const data: DataType[] =
    registeredConventions?.map((convention) => ({
      key: convention.conventionId,
      registrationNumber: convention.registrationNumber,
      studentName: convention.studentName,
      studentGroup: convention.studentGroup,
      organization: convention.organizationName,
      tutor: convention.tutorName,
      isFinished: convention.rectorSigned,
      registrationDate: convention.registrationDate,
      internshipStartDate: convention.internshipStartDate,
      internshipEndDate: convention.internshipEndDate,
      operations: ['download', 'viewPdf'],
    })) ?? [];

  // rowSelection object indicates the need for row selection
  const rowSelection: TableProps<DataType>['rowSelection'] = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: DataType[]) => {
      setSelectedRowCount(selectedRowKeys.length);
      setSelectedRows(selectedRows);
    },
  };

  const handleDownloadSelected = async () => {
    const selectedConventionIds: number[] = selectedRows.map((row) =>
      Number(row.key)
    );

    try {
      const response = await DownloadRegisteredConventionsZip(
        selectedConventionIds
      );
      const currentDate = new Date();
      const formattedDate = format(currentDate, 'dd-MM-yyyy_HH-mm-ss');

      downloadDocuments(response, `conventii_${formattedDate}`, 'zip');
    } catch {
      messageApi.error(t('legDepComp.downloadFail'));
    }
  };

  return (
    <div>
      {contextHolder}

      {selectedRowCount > 0 && (
        <Flex
          justify="space-between"
          align="center"
          style={{
            backgroundColor: token.colorFillSecondary,
            paddingLeft: 12,
          }}
        >
          <Typography.Text style={{ color: token.colorTextLabel, margin: 5 }}>
            {t('legDepComp.selectedRows')}: {selectedRowCount}
          </Typography.Text>
          <Button
            type="primary"
            icon={<LuDownload />}
            onClick={handleDownloadSelected}
            style={{ margin: 5, borderRadius: 5 }}
          >
            {t('legDepComp.downloadSelected')}
          </Button>
        </Flex>
      )}

      <ConfigProvider
        theme={{
          components: {
            Table: {
              cellPaddingBlockSM: 0,
            },
          },
        }}
      >
        <Table<DataType>
          rowSelection={{ type: 'checkbox', ...rowSelection }}
          columns={columns}
          dataSource={data}
          scroll={{ x: 'max-content' }}
          size="small"
          pagination={{
            defaultPageSize: 15,
            position: ['bottomCenter'],
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '15', '30', '50', '100', '200'],
          }}
        />
      </ConfigProvider>
    </div>
  );
};
